import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDollarSign, faChartLine, faUsers, faCalendarAlt, faHashtag, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons'
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import api from '@/services/api'
import Spinner from '@/components/Spinner'

interface RevenueStats {
  totalRevenue: number
  totalPaymentsCount: number
  thisMonthRevenue: number
  thisMonthCount: number
  thisQuarterRevenue: number
  thisYearRevenue: number
  mrr: number
  arr: number
  averageRevenuePerPayment: number
}

interface Payment {
  id: string
  organizer: {
    email: string
    name: string
    organization: string | null
    planType: string
  }
  type: 'monthly_subscription' | 'single_event'
  amount: number
  currency: string
  status: 'completed' | 'active' | 'canceled' | 'pending'
  planType?: string | null // Plan type at time of payment (for subscriptions)
  purchasedAt: string
  expiresAt?: string | null
  eventId?: string | null
  stripeSubscriptionId?: string | null
  stripePaymentIntentId?: string | null
  stripeCheckoutSessionId?: string | null
}

interface PaymentsResponse {
  payments: Payment[]
  total: number
  limit: number
  offset: number
}

type PeriodFilter = 'all' | 'month' | 'quarter' | 'year'

/**
 * Revenue Analytics Page
 * Dark mode geek aesthetic with charts and detailed payment table
 */
export default function RevenuePage() {
  const { t } = useTranslation()
  const [revenueStats, setRevenueStats] = useState<RevenueStats | null>(null)
  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(true)
  const [paymentsLoading, setPaymentsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | 'monthly_subscription' | 'single_event'>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'active' | 'canceled' | 'pending'>('all')
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' })

  // Pagination
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0 })

  // Format revenue amount in French format
  const formatRevenue = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '€0,00'
    }
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  // Fetch revenue stats
  useEffect(() => {
    const fetchRevenueStats = async () => {
      try {
        const response = await api.get('/superadmin/revenue/stats')
        if (response.data.success) {
          setRevenueStats(response.data.data)
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    fetchRevenueStats()
  }, [t])

  // Fetch payments with filters
  useEffect(() => {
    const fetchPayments = async () => {
      setPaymentsLoading(true)
      try {
        const params: any = {
          limit: pagination.pageSize,
          offset: (pagination.page - 1) * pagination.pageSize,
        }

        if (typeFilter !== 'all') {
          params.type = typeFilter
        }
        if (statusFilter !== 'all') {
          params.status = statusFilter
        }
        if (dateRange.start) {
          params.startDate = dateRange.start
        }
        if (dateRange.end) {
          params.endDate = dateRange.end
        }

        const response = await api.get('/superadmin/revenue/payments', { params })
        if (response.data.success) {
          const data: PaymentsResponse = response.data.data
          // Apply search filter client-side
          let filteredPayments = data.payments
          if (searchTerm) {
            const lower = searchTerm.toLowerCase()
            filteredPayments = data.payments.filter(
              (p) =>
                p.organizer.email.toLowerCase().includes(lower) ||
                p.organizer.name.toLowerCase().includes(lower) ||
                (p.organizer.organization && p.organizer.organization.toLowerCase().includes(lower))
            )
          }
          setPayments(filteredPayments)
          setPagination((prev) => ({ ...prev, total: data.total }))
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setPaymentsLoading(false)
      }
    }

    fetchPayments()
  }, [pagination.page, pagination.pageSize, typeFilter, statusFilter, dateRange, t])

  // Calculate chart data (recalculate when payments change)
  const chartData = useMemo(() => {
    if (!payments || payments.length === 0) {
      return {
        timelineData: [],
        typeDistribution: [{ name: 'Abonnements', value: 0 }, { name: 'Événements uniques', value: 0 }],
        planDistribution: [
          { name: 'PRO', value: 0 },
          { name: 'PREMIUM', value: 0 },
          { name: 'ULTIMATE', value: 0 },
          { name: 'ONE_SHOT', value: 0 },
        ],
        topOrganizers: [],
      }
    }

    return {
      // Timeline revenus par mois (calculé depuis les paiements)
      timelineData: (() => {
      const monthMap = new Map<string, number>()
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
      
      // Group payments by month (include both 'completed' and 'active' payments)
      payments
        .filter((p) => p.status === 'completed' || p.status === 'active')
        .forEach((p) => {
          const date = new Date(p.purchasedAt)
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
          const current = monthMap.get(monthKey) || 0
          monthMap.set(monthKey, current + p.amount)
        })

      // Get last 6 months
      const now = new Date()
      const last6Months = Array.from({ length: 6 }, (_, i) => {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        return {
          month: monthNames[date.getMonth()],
          revenue: monthMap.get(monthKey) || 0,
        }
      }).reverse()

        return last6Months
      })(),
      // Répartition par type (include both 'completed' and 'active' payments)
      typeDistribution: [
      {
        name: 'Abonnements',
        value: payments
          .filter((p) => p.type === 'monthly_subscription' && (p.status === 'completed' || p.status === 'active'))
          .reduce((sum, p) => sum + p.amount, 0),
      },
      {
        name: 'Événements uniques',
        value: payments
          .filter((p) => p.type === 'single_event' && (p.status === 'completed' || p.status === 'active'))
          .reduce((sum, p) => sum + p.amount, 0),
      },
      ],
      // Répartition par plan (depuis les paiements, include both 'completed' and 'active' payments)
      planDistribution: [
      {
        name: 'PRO',
        value: payments
          .filter((p) => p.organizer.planType === 'pro' && (p.status === 'completed' || p.status === 'active'))
          .reduce((sum, p) => sum + p.amount, 0),
      },
      {
        name: 'PREMIUM',
        value: payments
          .filter((p) => p.organizer.planType === 'premium' && (p.status === 'completed' || p.status === 'active'))
          .reduce((sum, p) => sum + p.amount, 0),
      },
      {
        name: 'ULTIMATE',
        value: payments
          .filter((p) => p.organizer.planType === 'ultimate' && (p.status === 'completed' || p.status === 'active'))
          .reduce((sum, p) => sum + p.amount, 0),
      },
      {
        name: 'ONE_SHOT',
        value: payments
          .filter((p) => p.type === 'single_event' && (p.status === 'completed' || p.status === 'active'))
          .reduce((sum, p) => sum + p.amount, 0),
      },
      ],
      // Top 10 organisateurs (include both 'completed' and 'active' payments)
      topOrganizers: (() => {
      const organizerMap = new Map<string, { name: string; revenue: number }>()
      payments
        .filter((p) => p.status === 'completed' || p.status === 'active')
        .forEach((p) => {
          const key = p.organizer.email
          const current = organizerMap.get(key) || { name: p.organizer.name, revenue: 0 }
          organizerMap.set(key, { ...current, revenue: current.revenue + p.amount })
        })
      return Array.from(organizerMap.values())
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10)
      })(),
    }
  }, [payments])

  const COLORS = {
    cyan: '#22d3ee',
    purple: '#a855f7',
    pink: '#ec4899',
    green: '#10b981',
    amber: '#f59e0b',
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
        <p className="text-red-300 font-mono text-sm">
          <span className="text-red-500">{'[ERROR]'}</span> {error}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-4xl font-black bg-linear-to-r from-green-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
          Revenus & Analytics
        </h1>
        <p className="text-slate-400 mt-2 font-mono text-sm">
          {'> '} <span className="text-green-400">revenue</span>{' --analytics --detailed'}
        </p>
      </div>

      {/* KPIs Cards */}
      <div className="grid grid-cols-5 gap-6">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <p className="text-slate-400 text-xs font-mono uppercase tracking-wider">Revenus totaux</p>
          <p className="text-3xl font-black text-green-400 mt-3">{formatRevenue(revenueStats?.totalRevenue)}</p>
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <FontAwesomeIcon icon={faDollarSign} />
            <span>Tous temps</span>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <p className="text-slate-400 text-xs font-mono uppercase tracking-wider">MRR</p>
          <p className="text-3xl font-black text-cyan-400 mt-3">{formatRevenue(revenueStats?.mrr)}</p>
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <FontAwesomeIcon icon={faChartLine} />
            <span>Mensuel récurrent</span>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <p className="text-slate-400 text-xs font-mono uppercase tracking-wider">ARR</p>
          <p className="text-3xl font-black text-purple-400 mt-3">{formatRevenue(revenueStats?.arr)}</p>
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>Annuel récurrent</span>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <p className="text-slate-400 text-xs font-mono uppercase tracking-wider">Paiements</p>
          <p className="text-3xl font-black text-pink-400 mt-3">{revenueStats?.totalPaymentsCount || 0}</p>
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <FontAwesomeIcon icon={faHashtag} />
            <span>Total</span>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <p className="text-slate-400 text-xs font-mono uppercase tracking-wider">Moyenne</p>
          <p className="text-3xl font-black text-amber-400 mt-3">{formatRevenue(revenueStats?.averageRevenuePerPayment)}</p>
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <FontAwesomeIcon icon={faChartLine} />
            <span>Par paiement</span>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Timeline Revenus */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 font-mono">
            {'> '}
            <span className="text-cyan-400">timeline</span>
          </h2>
          {chartData.timelineData.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-slate-500 font-mono text-sm">
              Aucune donnée disponible
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.timelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                <XAxis dataKey="month" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" tickFormatter={(value) => `€${value}`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', color: '#e2e8f0' }}
                  formatter={(value: number | undefined) => formatRevenue(value ?? 0)}
                />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke={COLORS.cyan} strokeWidth={2} name="Revenus" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Répartition par Type */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 font-mono">
            {'> '}
            <span className="text-purple-400">type</span>
          </h2>
          {chartData.typeDistribution.every((d) => d.value === 0) ? (
            <div className="flex items-center justify-center h-[300px] text-slate-500 font-mono text-sm">
              Aucune donnée disponible
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.typeDistribution.filter((d) => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.typeDistribution
                    .filter((d) => d.value > 0)
                    .map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index === 0 ? COLORS.cyan : COLORS.purple} />
                    ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', color: '#e2e8f0' }}
                  formatter={(value: number | undefined) => formatRevenue(value ?? 0)}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Répartition par Plan */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 font-mono">
            {'> '}
            <span className="text-pink-400">plans</span>
          </h2>
          {chartData.planDistribution.every((d) => d.value === 0) ? (
            <div className="flex items-center justify-center h-[300px] text-slate-500 font-mono text-sm">
              Aucune donnée disponible
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.planDistribution.filter((d) => d.value > 0)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                <XAxis dataKey="name" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" tickFormatter={(value) => `€${value}`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', color: '#e2e8f0' }}
                  formatter={(value: number | undefined) => formatRevenue(value ?? 0)}
                />
                <Bar dataKey="value" fill={COLORS.green} name="Revenus" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top 10 Organisateurs */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 font-mono">
            {'> '}
            <span className="text-amber-400">top_organizers</span>
          </h2>
          {chartData.topOrganizers.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-slate-500 font-mono text-sm">
              Aucune donnée disponible
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.topOrganizers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                <XAxis type="number" stroke="#94a3b8" tickFormatter={(value) => `€${value}`} />
                <YAxis dataKey="name" type="category" stroke="#94a3b8" width={120} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', color: '#e2e8f0' }}
                  formatter={(value: number | undefined) => formatRevenue(value ?? 0)}
                />
                <Bar dataKey="revenue" fill={COLORS.amber} name="Revenus" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white font-mono">
                {'> '}
                <span className="text-green-400">payments</span>
                {' --detailed'}
              </h2>
              <p className="text-slate-400 text-xs mt-1 font-mono">
                Liste complète des paiements ({pagination.total})
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-slate-700/50 bg-slate-800/30">
          <div className="grid grid-cols-5 gap-4">
            {/* Search */}
            <div>
              <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
                Recherche
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Email, nom, organisation..."
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
              />
            </div>

            {/* Type Filter */}
            <div>
              <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
                Type
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
              >
                <option value="all">Tous</option>
                <option value="monthly_subscription">Abonnements</option>
                <option value="single_event">Événements uniques</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
                Statut
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
              >
                <option value="all">Tous</option>
                <option value="completed">Complété</option>
                <option value="active">Actif</option>
                <option value="canceled">Annulé</option>
                <option value="pending">En attente</option>
              </select>
            </div>

            {/* Date Start */}
            <div>
              <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
                Date début
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
              />
            </div>

            {/* Date End */}
            <div>
              <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
                Date fin
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {paymentsLoading ? (
          <div className="flex justify-center py-24">
            <Spinner />
          </div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center text-slate-400 font-mono">
            {'$ '} Aucun paiement trouvé
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-800/50 border-b border-slate-700/50">
                    <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                      Organisateur
                    </th>
                    <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                      Montant
                    </th>
                    <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                      Stripe
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-slate-800/50 transition">
                      <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                        {new Date(payment.purchasedAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs">
                          <p className="text-slate-200 font-semibold">{payment.organizer.name}</p>
                          <p className="text-slate-400">{payment.organizer.email}</p>
                          {payment.organizer.organization && (
                            <p className="text-slate-500">{payment.organizer.organization}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {payment.type === 'monthly_subscription' ? (
                          <span className="px-2 py-1 bg-blue-900/30 text-blue-300 rounded text-xs">
                            Abonnement
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-purple-900/30 text-purple-300 rounded text-xs">
                            Événement unique
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-300 font-mono text-xs capitalize">
                        {(() => {
                          // Use planType from payment (plan at time of payment) if available, otherwise fallback to organizer's current plan
                          const displayPlanType = payment.planType || payment.organizer.planType
                          if (displayPlanType === 'free') return 'Gratuit'
                          if (displayPlanType === 'pro') return 'Pro'
                          if (displayPlanType === 'premium') return 'Premium'
                          if (displayPlanType === 'ultimate') return 'Ultimate'
                          if (displayPlanType === 'one_shot') return 'Événement unique'
                          return displayPlanType || 'N/A'
                        })()}
                      </td>
                      <td className="px-6 py-4 text-green-400 font-bold font-mono">
                        {formatRevenue(payment.amount)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-bold ${
                            payment.status === 'completed'
                              ? 'bg-green-900/50 text-green-300 border border-green-700/50'
                              : payment.status === 'active'
                              ? 'bg-blue-900/50 text-blue-300 border border-blue-700/50'
                              : payment.status === 'canceled'
                              ? 'bg-red-900/50 text-red-300 border border-red-700/50'
                              : 'bg-yellow-900/50 text-yellow-300 border border-yellow-700/50'
                          }`}
                        >
                          {payment.status === 'completed'
                            ? 'Complété'
                            : payment.status === 'active'
                            ? 'Actif'
                            : payment.status === 'canceled'
                            ? 'Annulé'
                            : 'En attente'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {(payment.stripeSubscriptionId || payment.stripePaymentIntentId) ? (
                          <a
                            href={`https://dashboard.stripe.com/test${
                              payment.stripeSubscriptionId
                                ? `/subscriptions/${payment.stripeSubscriptionId}`
                                : `/payments/${payment.stripePaymentIntentId}`
                            }`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300 transition text-xs font-mono inline-flex items-center gap-1"
                          >
                            <FontAwesomeIcon icon={faUpRightFromSquare} />
                            Stripe
                          </a>
                        ) : (
                          <span className="text-slate-500 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-slate-700/50 bg-slate-800/30 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-slate-400 text-xs font-mono">
                  Affichage {((pagination.page - 1) * pagination.pageSize) + 1} -{' '}
                  {Math.min(pagination.page * pagination.pageSize, pagination.total)} sur {pagination.total}
                </span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) =>
                    setPagination((prev) => ({ ...prev, pageSize: Number(e.target.value), page: 1 }))
                  }
                  className="px-3 py-1 bg-slate-800 border border-slate-600 rounded text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-300 font-mono text-xs hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Précédent
                </button>
                <span className="text-slate-400 text-xs font-mono">
                  Page {pagination.page} / {Math.ceil(pagination.total / pagination.pageSize)}
                </span>
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
                  className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-300 font-mono text-xs hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Suivant
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
